import React from "react";
import LegalShell from "./LegalShell";

// Clauses financières v2.0 — alignées sur les paramètres effectivement configurés
// dans l'application (backend/finance.py) : commission max(15 %, 25 €), acompte 25 %,
// solde à J-3, fenêtre 72 h, frais de dossier 15 €, retenue 50 %, SLA litige 7 j ouvrés.
export default function CGV() {
  return (
    <LegalShell
      title="Conditions Générales de Vente"
      subtitle="Clauses financières : prix, séquestre en deux temps, acompte, annulation, validation de livraison et litiges."
      lastUpdate="2 août 2026 (v2.0)"
      canonical="https://depannroute.com/cgv"
    >
      <h2>1. Champ d'application et définitions</h2>
      <p>
        Les présentes CGV s'appliquent à toute Mission de transport de véhicule conclue via
        DepannRoute, exploité par KPITAL LIMITED. La <strong>Mission</strong> est une prestation de
        transport entre un point de départ et un point d'arrivée ; un <strong>Mouvement</strong> de
        camion peut regrouper deux Missions distinctes (un aller et un retour), juridiquement et
        financièrement indépendantes. Le <strong>Séquestre</strong> désigne la détention des fonds
        par le prestataire de paiement entre l'encaissement et le reversement au Dépanneur.
      </p>

      <h2>2. Prix et commission</h2>
      <ul>
        <li>Le prix présenté au Client est un montant unique, toutes taxes comprises, incluant la rémunération de la Plateforme. Aucune commission n'est facturée séparément au Client.</li>
        <li>La Plateforme perçoit une commission d'intermédiation de <strong>15 % du prix de chaque Mission, avec un minimum forfaitaire de 25 €</strong>, prélevée automatiquement au moment du reversement des fonds au Dépanneur.</li>
        <li>Le prix minimum d'une offre est de 30 €.</li>
        <li>L'encaissement, le séquestre et le reversement sont opérés par <strong>Stripe</strong> (Stripe Connect), prestataire de services de paiement agréé. La Plateforme ne détient jamais les fonds pour compte de tiers en son nom propre.</li>
      </ul>

      <h2>3. Séquestre en deux temps</h2>
      <ul>
        <li>À la validation de l'offre, un <strong>acompte de 25 % du prix</strong> est encaissé. Si le chargement est prévu dans les 3 jours, la totalité du prix est encaissée à la validation.</li>
        <li>Le <strong>solde est débité automatiquement 3 jours avant la date de chargement</strong>, sur le moyen de paiement enregistré. Le Client autorise expressément cet enregistrement et ce débit différé par une case d'acceptation dédiée au moment du paiement ; il est notifié avant et après le débit.</li>
        <li>En cas d'échec du débit du solde, le Client est relancé avec un lien de régularisation. Si l'échec persiste 2 jours avant le chargement, la Mission est annulée de plein droit : l'acompte demeure acquis (frais de gestion et indemnisation du Dépanneur) et le Dépanneur est prévenu avant tout début d'exécution.</li>
        <li>Les fonds séquestrés sont indisponibles pour les deux parties et ne produisent pas d'intérêts.</li>
      </ul>

      <h2>4. Annulation par le Client</h2>
      <p>Les conditions dépendent du délai séparant l'annulation du chargement prévu :</p>
      <ul>
        <li><strong>Plus de 72 h avant chargement :</strong> remboursement intégral, déduction faite de frais de dossier de 15 €. Le solde non encore débité n'est pas encaissé.</li>
        <li><strong>Entre 24 h et 72 h :</strong> l'acompte (25 %) est conservé — frais de dossier et indemnité du Dépanneur — et le solde est remboursé.</li>
        <li><strong>Moins de 24 h ou non-présentation :</strong> l'acompte est conservé et une retenue de 50 % est appliquée sur le solde ; le reste est remboursé.</li>
      </ul>
      <p>
        Un récapitulatif chiffré (montants remboursés et conservés) est présenté au Client avant
        toute confirmation d'annulation. Après chargement du véhicule, l'annulation n'est plus
        possible : la procédure de contestation de livraison s'applique (article 6). Les sommes
        conservées se répartissent entre les frais de dossier de la Plateforme et l'indemnité due
        au Dépanneur au titre de son manque à gagner.
      </p>
      <p>
        La prestation de transport étant fixée à une date déterminée, elle relève des exceptions au
        droit de rétractation applicables aux services de transport (art. L.221-28 C. consommation).
      </p>

      <h2>5. Annulation par le Dépanneur</h2>
      <p>
        La validation d'une offre constitue un engagement ferme. En cas d'annulation par le
        Dépanneur, hors force majeure (art. 1218 C. civil) dûment justifiée :
      </p>
      <ul>
        <li>le Client est <strong>intégralement remboursé, acompte inclus</strong>, et sa demande est republiée en priorité auprès des dépanneurs de la zone ;</li>
        <li>le Dépanneur supporte une pénalité financière progressive selon la proximité du chargement (25 € entre 24 h et 72 h, 50 € sous 24 h), prélevée sur ses reversements futurs, ainsi qu'un malus de classement dans le moteur d'appariement ;</li>
        <li>au-delà de 3 annulations sur 90 jours glissants, le compte est suspendu 30 jours, puis définitivement en cas de récidive.</li>
      </ul>

      <h2>6. Livraison, preuve photo et validation tacite</h2>
      <ul>
        <li>Le déblocage des fonds est conditionné à des <strong>photographies contradictoires</strong> de l'état du véhicule au chargement et à la livraison, horodatées et scellées numériquement (empreinte SHA-256). Sans photos d'arrivée, aucun reversement n'est déclenché.</li>
        <li>À compter du téléversement des photos d'arrivée, le Client dispose de <strong>72 heures</strong> pour confirmer ou contester la livraison. <strong>À défaut d'action dans ce délai, la livraison est réputée conforme</strong> et le reversement au Dépanneur est déclenché automatiquement. Le Client est notifié de l'ouverture et du terme de ce délai.</li>
        <li>Lorsqu'un Mouvement regroupe deux Missions, chaque Mission donne lieu à un déblocage distinct et indépendant : le litige de l'une ne bloque jamais les fonds de l'autre.</li>
        <li>Le versement effectif au Dépanneur intervient sous 2 à 3 jours ouvrés après le déclenchement du reversement, net de commission.</li>
      </ul>

      <h2>7. Litiges et contestations</h2>
      <ul>
        <li>La contestation d'une livraison dans le délai de 72 h gèle les fonds de la seule Mission concernée et ouvre une médiation fondée sur les photographies contradictoires.</li>
        <li>La Plateforme instruit tout litige sous <strong>7 jours ouvrés</strong> ; à défaut, le dossier est transmis automatiquement à un niveau d'examen supérieur. La médiation peut décider d'un déblocage total, d'un déblocage partiel ou d'un remboursement.</li>
        <li>Les oppositions bancaires (chargeback) relèvent de la procédure du prestataire de paiement, distincte de la médiation interne ; le Client s'engage à privilégier la procédure de la Plateforme.</li>
        <li>Les contestations répétées et manifestement infondées peuvent donner lieu à des frais de médiation.</li>
      </ul>

      <h2>8. Conformité des Dépanneurs</h2>
      <p>
        L'accès des Dépanneurs aux Missions est subordonné au maintien en cours de validité de leurs
        documents réglementaires (pièce d'identité, Kbis, attestation URSSAF, RIB, permis, carte de
        qualification de conducteur, licence de transport intérieur, assurances RC professionnelle et
        véhicule, contrôle technique — autorisation préfectorale le cas échéant). Ces documents sont
        vérifiés par la Plateforme et scellés numériquement ; un document expiré rend le Dépanneur
        inéligible à toute nouvelle Mission jusqu'à renouvellement, sans effet sur les Missions en cours.
      </p>

      <h2>9. Responsabilité et force majeure</h2>
      <p>
        DepannRoute agit en qualité d'intermédiaire et reste garante du séquestre et de la preuve
        jusqu'au reversement. La bonne exécution du transport incombe au Dépanneur, couvert par son
        assurance RC professionnelle. Aucune partie ne répond d'un manquement dû à la force majeure
        au sens de l'article 1218 du Code civil.
      </p>

      <h2>10. Droit applicable</h2>
      <p>
        Les présentes CGV sont soumises au droit français, sous réserve des dispositions impératives
        protégeant le consommateur. Tout litige sera porté devant les tribunaux compétents, après
        tentative de résolution amiable via le médiateur indiqué dans les{" "}
        <a href="/mentions-legales">Mentions légales</a>.
      </p>
    </LegalShell>
  );
}
